import { useQueryClient } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef } from "react";

import {
	type AskMessage,
	AskSSEEventSchema,
	type AskSSEInterviewerOutputEvent,
	type InterviewerHarnessType,
	MAX_CURRENT_FORM_NAME_LENGTH,
	MAX_CURRENT_FORM_QUESTIONS,
	MAX_CURRENT_FORM_QUESTION_LENGTH,
	MAX_CURRENT_FORM_SUBTEXT_LENGTH,
} from "@performyard/service-contracts";

import { useEditFormStore } from "../../../pages/Administration/Reviews/EditForm/useEditFormStore";

import type { FormBuilderContextSnapshot } from "../types";

import { getInterviewerNotesKey } from "../store/helpers";

import { ASK_CONVERSATIONS_QUERY_KEY } from "../../../api/hooks";
import { useFormAuthoringCoachEnabled } from "./useFormAuthoringCoachEnabled";

import { getHeaders, getUrl } from "../../../api/services/defs/internal-api/internal-base-request";

import { extractSSEMessages, parseSSEMessage } from "../utils";
import { collectReviewAnswers } from "../utils/collectReviewAnswers";
import { isCompetencyReimportBlocked } from "../utils/is-competency-reimport-blocked";

import { useAskPerformYardStore } from "../AskPerformYardStore";

// Idle timeout in milliseconds — reset on each received event (2 minutes)
const STREAM_TIMEOUT_MS = 120_000;

export type SendQueryOptions = {
	query: string;
	messages?: AskMessage[];
	agent?: "form" | "goal-coach" | "form-building-coach" | "form-authoring-coach";
	/** Routes this turn to an interviewer harness regardless of the active one. */
	harness?: InterviewerHarnessType;
	documentContent?: string;
	/** Retrying a question already in the thread — skip appending a duplicate user bubble. */
	omitUserMessage?: boolean;
};

type UseAskStreamReturn = {
	sendQuery: (options: SendQueryOptions) => Promise<void>;
	abort: () => void;
	isStreaming: boolean;
};

/**
 * One in-flight request. Held per stream rather than in a single ref (PY-13559):
 * a user who sends in one conversation and then sends in another has two live
 * streams, and a single ref meant starting the second aborted the first —
 * truncating an answer the first conversation had already committed to.
 */
type ActiveStream = {
	controller: AbortController;
	timeout: ReturnType<typeof setTimeout> | null;
	reader: ReadableStreamDefaultReader<Uint8Array> | null;
	/**
	 * The conversation this stream's pending turn is filed under, re-pointed when
	 * the `conversation` event re-keys the stream. Held here so tearing down this
	 * hook clears only the pending turns it can no longer complete — the store,
	 * and the other `useAskStream` instances on the page, outlive any one of them.
	 */
	conversationId: string | null;
};

/** Hook to send queries and handle SSE streaming responses. */
export const useAskStream = (): UseAskStreamReturn => {
	const activeStreamsRef = useRef(new Map<string, ActiveStream>());
	// Nothing but the coach reads the review context this hook attaches below.
	const formAuthoringCoachEnabled = useFormAuthoringCoachEnabled();

	/**
	 * Abort one stream and drop the pending turn it can no longer finish.
	 *
	 * Scoped to this stream on purpose. This hook is mounted more than once per
	 * page (the panel, the full-page chat container, and the composer inside
	 * each), every instance sharing one store, so clearing *all* pending turns
	 * here would let the composer unmounting on panel close wipe the record of a
	 * turn another instance still has in flight — and `appendPendingTurn` no-ops
	 * once the record is gone, so returning to that conversation would show a
	 * thread that forgot the question.
	 */
	const teardownStream = (stream: ActiveStream): void => {
		stream.controller.abort();
		if (stream.timeout) clearTimeout(stream.timeout);
		void stream.reader?.cancel();
		if (stream.conversationId) {
			useAskPerformYardStore.getState().completePendingTurn(stream.conversationId);
		}
	};

	// Cleanup on unmount to prevent memory leaks
	useEffect(() => {
		const activeStreams = activeStreamsRef.current;
		return () => {
			// Only this instance's streams: a pending record left behind by one of
			// them has nothing to complete it and would reopen as a bubble stuck
			// streaming, but records owned by another instance are still live.
			for (const stream of activeStreams.values()) teardownStream(stream);
			activeStreams.clear();
		};
	}, []);

	// Subscribe to status for isStreaming check
	const status = useAskPerformYardStore((state) => state.status);

	const queryClient = useQueryClient();

	// Get actions via getState() to avoid stale closures in async operations
	// This is the recommended Zustand pattern for actions used in callbacks
	const getStoreActions = useCallback(() => useAskPerformYardStore.getState(), []);

	/** Stop every in-flight stream. Used on teardown, not on conversation switch. */
	const abort = useCallback(() => {
		// teardownStream completes each stream's own pending turn; nothing here
		// touches turns belonging to another useAskStream instance.
		for (const stream of activeStreamsRef.current.values()) teardownStream(stream);
		activeStreamsRef.current.clear();
		const { setStatus, setStreamStage, setStreamMessage } = getStoreActions();
		setStatus("idle");
		setStreamStage(null);
		setStreamMessage(null);
	}, [getStoreActions]);

	const sendQuery = useCallback(
		async ({
			query,
			messages = [],
			agent,
			harness,
			documentContent,
			omitUserMessage = false,
		}: SendQueryOptions): Promise<void> => {
			// Get fresh store state and actions to avoid stale closures
			const {
				sessionId,
				conversationId,
				setActiveConversation,
				addUserMessage,
				startAssistantMessage,
				appendToAssistantMessage,
				completeAssistantMessage,
				markCompetencyIntro,
				finalizeMessage,
				setCompetencySuggestions,
				applyCompetencyEdit,
				setReportConfig,
				setStatus,
				setStreamStage,
				setStreamMessage,
				setError,
				setGoalSuggestions,
				setGoalImprovements,
				setFormSuggestions,
				setFormQuestionSuggestions,
				setFormImprovements,
				setResponseImprovements,
				activeInterviewerHarness,
				setActiveInterviewerHarness,
				deactivateInterviewerHarness,
				startPendingTurn,
				appendPendingTurn,
				recordPendingTurnSuggestions,
				completePendingTurn,
			} = getStoreActions();

			// A caller-supplied harness owns the turn even when no interview is active.
			const effectiveHarness = harness ?? activeInterviewerHarness;
			if (harness && harness !== activeInterviewerHarness) {
				setActiveInterviewerHarness(harness);
			}

			// The conversation this stream belongs to. Null until the `conversation`
			// event names it on a brand-new chat.
			let streamConversationId: string | null = conversationId;

			/**
			 * Whether this stream's conversation is the one on screen.
			 *
			 * Every write to the visible thread goes through this. A stream whose
			 * conversation the user has navigated away from keeps running — the
			 * server still persists it, and its text is kept as a pending turn — but
			 * it must not touch the thread the user is now looking at, or the status
			 * bar above it.
			 */
			const ownsVisibleThread = (): boolean => {
				const store = useAskPerformYardStore.getState();
				// Same reasoning as the `adoptable` check below: before the
				// `conversation` event names this turn's conversation, `null` is not
				// an identity — `resetChat` also sets the store's id to null, so a
				// New Chat started mid-stream would otherwise look like the chat that
				// sent this turn and inherit its status, error and streamed text.
				// `sessionId` is the discriminator because `resetChat` mints a fresh
				// one. This matters most with conversation history off, where the
				// re-keying `conversation` event never arrives at all.
				return streamConversationId === null
					? store.conversationId === null && store.sessionId === sessionId
					: store.conversationId === streamConversationId;
			};

			const HARNESS_OUTPUT_HANDLERS: {
				[HARNESS_TYPE in AskSSEInterviewerOutputEvent["harness"]]: (
					event: Extract<AskSSEInterviewerOutputEvent, { harness: HARNESS_TYPE }>,
					msgId: string
				) => void;
			} = {
				competency: (event, msgId) => setCompetencySuggestions(msgId, event.suggestions),
				reporting_analytics_report: (event, msgId) => {
					setReportConfig(msgId, event.reportConfig);
					// Submit completes the interview — clear the active harness so the next user
					// message routes through the classifier instead of re-entering the interviewer.
					deactivateInterviewerHarness();
				},
			};

			// Deliberately does NOT abort other in-flight streams. A turn already sent
			// in another conversation has to finish there — aborting it would drop the
			// client, and the server would persist only the partial answer.
			const streamKey = `stream-${nanoid()}`;
			const abortController = new AbortController();
			const activeStream: ActiveStream = {
				controller: abortController,
				timeout: null,
				reader: null,
				conversationId: streamConversationId,
			};
			activeStreamsRef.current.set(streamKey, activeStream);

			const releaseStream = (): void => {
				const stream = activeStreamsRef.current.get(streamKey);
				if (stream?.timeout) clearTimeout(stream.timeout);
				activeStreamsRef.current.delete(streamKey);
			};

			// Add user message to store (a retry already has its bubble in the thread)
			if (!omitUserMessage) {
				addUserMessage(query);
			}

			// Clear any previous error/stream state and set thinking status
			setError(null);
			setStreamStage(null);
			setStreamMessage(null);
			setStatus("thinking");

			// Push the empty streaming assistant message NOW (not after fetch returns)
			// so the in-message "Thinking…" bubble owns the placeholder from the
			// very first frame. Without this, a separately-styled placeholder would
			// render until startAssistantMessage runs post-fetch, causing a visible
			// size/style flash when the real message bubble takes over.
			const assistantMessageId: string = startAssistantMessage();

			// A brand-new chat has no id yet, so its pending record is opened when the
			// `conversation` event arrives. A continued one can be recorded now.
			if (streamConversationId) {
				startPendingTurn(streamConversationId, {
					userContent: query,
					assistantMessageId,
					assistantContent: "",
				});
			}

			// Idle timeout, re-armed on each received event so a long but active
			// stream never hard-fails. Armed after the message exists so the handler
			// can finalize it — otherwise a timed-out request leaves the message
			// stuck showing the streaming cursor. Held on this stream's record
			// rather than a shared ref, because concurrent streams each run their
			// own clock and their own controller.
			let suppressHarnessEntryOutput = false;
			const armIdle = (): void => {
				if (activeStream.timeout) clearTimeout(activeStream.timeout);
				activeStream.timeout = setTimeout(() => {
					abortController.abort();
					if (streamConversationId) completePendingTurn(streamConversationId);
					// Only surface the failure if the user is still looking at this
					// conversation; otherwise it belongs to a thread off screen.
					if (ownsVisibleThread()) {
						const actions = getStoreActions();
						actions.completeAssistantMessage(assistantMessageId);
						actions.setError("Request timed out. Please try again.");
					}
				}, STREAM_TIMEOUT_MS);
			};
			armIdle();

			try {
				const url = getUrl("/v1/ai/ask/stream");
				const headers = getHeaders({ body: JSON.stringify({}) });
				const interviewNotes = effectiveHarness
					? (() => {
							try {
								const raw = sessionStorage.getItem(getInterviewerNotesKey(effectiveHarness));
								return raw ? JSON.parse(raw) : null;
							} catch {
								return null;
							}
						})()
					: null;

				// Pending suggestions from earlier turns — re-sent so the agent can
				// iterate without losing question-level detail.
				// Restored cards are excluded: they record an earlier turn rather than a
				// live draft to iterate on, and replaying them routes the next message
				// into the form agent's iteration path instead of answering what was
				// asked.
				const priorFormSuggestions = useAskPerformYardStore
					.getState()
					.formSuggestionStates.filter((fs) => !fs.isRestored && fs.status === "pending")
					.map((fs) => fs.suggestion);

				const competencyFramework = useAskPerformYardStore.getState().competencyFramework;
				const reviewStatusOf = (id: string) => {
					const itemStatus = competencyFramework?.itemStatuses[id];
					return itemStatus === "created" || itemStatus === "removed" ? itemStatus : "pending";
				};
				const priorCompetencySuggestions = competencyFramework
					? competencyFramework.suggestions.map((suggestion) => ({
							...suggestion,
							...competencyFramework.editedFields[suggestion.id], // fold in unsaved edits
							reviewStatus: reviewStatusOf(suggestion.id),
						}))
					: [];
				// The rule belongs to the competency harness only; a classified turn can still enter it.
				const competencyImportBlocked =
					isCompetencyReimportBlocked(competencyFramework) &&
					(!effectiveHarness || effectiveHarness === "competency");
				// Send the in-progress form so the agent can tailor question suggestions.
				const { isOnFormBuilderPage, currentFormBuilderFormId } = useAskPerformYardStore.getState();
				let currentForm: FormBuilderContextSnapshot | undefined;
				if (isOnFormBuilderPage) {
					const { name, questions, editingQuestion, activeQuestionValuesReader } =
						useEditFormStore.getState();
					// An open edit card's values live in its form until saved — read them so the
					// snapshot reflects what's on screen, not the last-saved version.
					const liveValues = editingQuestion?.refId ? activeQuestionValuesReader?.() : null;
					currentForm = {
						// Stamped onto every question suggestion so a restored card can compare
						// against whatever form is open later.
						formId: currentFormBuilderFormId ?? undefined,
						// Sliced to the contract's payload caps — the builder has no question-count
						// limit, an open card can hold not-yet-validated overlong text, and an
						// oversized snapshot must degrade, not fail validation.
						name: name.slice(0, MAX_CURRENT_FORM_NAME_LENGTH),
						questions: questions.slice(0, MAX_CURRENT_FORM_QUESTIONS).map((storeQuestion) => {
							const q =
								liveValues && storeQuestion.refId === editingQuestion?.refId
									? { ...storeQuestion, ...liveValues }
									: storeQuestion;
							// choices is a union (string[] | RatingScaleOption[]): rating-scale questions keep the
							// structured options (the coach can suggest label/description rewrites); everything
							// else flattens to string labels.
							const ratingScale =
								q.type === "rating-scale"
									? q.choices?.flatMap((c) =>
											typeof c === "string"
												? []
												: [
														{
															value: c?.value ?? "",
															name: c?.name ?? "",
															...(c?.description ? { description: c.description } : {}),
														},
													]
										)
									: undefined;
							const choices =
								q.type === "rating-scale"
									? undefined
									: q.choices
											?.map((c) => (typeof c === "string" ? c : (c?.name ?? "")))
											.filter((c) => c.length > 0);
							return {
								id: q.refId,
								type: q.type,
								question: (q.question ?? "").slice(0, MAX_CURRENT_FORM_QUESTION_LENGTH),
								subtext: (q.subtext ?? "").slice(0, MAX_CURRENT_FORM_SUBTEXT_LENGTH),
								...(choices && choices.length > 0 ? { choices } : {}),
								...(ratingScale && ratingScale.length > 0 ? { rating_scale: ratingScale } : {}),
								...(q.is_required !== undefined ? { is_required: q.is_required } : {}),
							};
						}),
					};
				}

				// Forwarded whenever the chat is open on the goal page so classified turns also carry the goal.
				const { isOnGoalCreationPage, currentGoalSnapshot } = useAskPerformYardStore.getState();
				const currentGoal = isOnGoalCreationPage ? (currentGoalSnapshot ?? undefined) : undefined;

				// Forwarded whenever the chat is open on a review the user is authoring, so classified
				// turns reach the coach with a report to load. Only the id travels — the agent loads the
				// form, questions, and answers itself and re-checks that this user is its current author.
				//
				// Gated on the coach as well as the page: nothing else reads either field, so without the
				// flag a document or general turn would carry the whole answer snapshot for a company that
				// can't route to the coach at all — and a long review would fail those turns on body size.
				const { authoringReviewReportId } = useAskPerformYardStore.getState();
				const reviewReportId = formAuthoringCoachEnabled ? authoringReviewReportId : null;
				// Read at send time, not on mount: the point is to coach the text the author just typed
				// and hasn't saved.
				const currentAnswers = reviewReportId ? collectReviewAnswers() : [];

				const contextPayload: Record<string, unknown> = {};
				if (priorFormSuggestions.length > 0) contextPayload.priorFormSuggestions = priorFormSuggestions;
				if (currentForm) contextPayload.currentForm = currentForm;
				if (currentGoal) contextPayload.currentGoal = currentGoal;
				if (reviewReportId) contextPayload.reviewReportId = reviewReportId;
				if (currentAnswers.length > 0) contextPayload.currentAnswers = currentAnswers;
				if (competencyImportBlocked && !effectiveHarness) contextPayload.competencyImportBlocked = true;

				const response = await fetch(url, {
					method: "POST",
					headers: {
						...headers,
						"Content-Type": "application/json",
					} as Record<string, string>,
					body: JSON.stringify({
						query,
						sessionId,
						// Omitted on a new chat — the server mints the conversation and
						// names it back over the `conversation` event.
						...(conversationId ? { conversationId } : {}),
						messages: messages.map((m) => ({
							role: m.role,
							content: m.content,
						})),
						...(Object.keys(contextPayload).length > 0 ? { context: contextPayload } : {}),
						...(interviewNotes !== null && { interviewNotes }),
						...(priorCompetencySuggestions.length > 0 ? { priorCompetencySuggestions } : {}),
						...(competencyImportBlocked && effectiveHarness ? { competencyImportBlocked: true } : {}),
						...(documentContent ? { documentContent } : {}),
						...(effectiveHarness
							? { agent: "interviewer", harness: effectiveHarness }
							: agent
								? { agent }
								: {}),
					}),
					signal: abortController.signal,
				});

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(errorData.message || `Request failed: ${response.statusText}`);
				}

				if (!response.body) {
					throw new Error("Response body is null");
				}

				// Read the SSE stream
				const reader = response.body.getReader();
				activeStream.reader = reader;
				const decoder = new TextDecoder();
				let buffer = "";

				let streamDone = false;
				while (!streamDone) {
					const { done, value } = await reader.read();

					if (done) {
						streamDone = true;
						continue;
					}

					// Decode chunk and add to buffer
					buffer += decoder.decode(value, { stream: true });
					armIdle();

					// Extract and process complete SSE messages
					const { messages: sseMessages, remainingBuffer } = extractSSEMessages(buffer);
					buffer = remainingBuffer;

					for (const message of sseMessages) {
						const parsed = parseSSEMessage(message);

						if (!parsed) {
							continue;
						}

						const { eventType, data } = parsed;

						const result = AskSSEEventSchema.safeParse({ ...(data as object), type: eventType });
						if (!result.success) {
							// A dropped event is invisible in the UI — the turn just quietly lacks its cards or
							// status — so the reason has to be readable without expanding a nested error object.
							const issues = result.error.issues
								.map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`)
								.join("; ");
							// `data` is deliberately not logged: for responseImprovements it holds verbatim
							// review prose about a named employee, and console breadcrumbs ship the raw
							// argument objects to Sentry.
							console.warn(`[AskStream] Failed to parse SSE event "${eventType}" — ${issues}`, {
								eventType,
							});
							continue;
						}
						const event = result.data;

						// Payload events address a message in the visible thread. When the
						// user has moved to another conversation that message is not on
						// screen, so the write would either no-op or, worse, land on
						// whatever message happens to be last in the thread they moved to.
						const visible = ownsVisibleThread();

						switch (event.type) {
							case "status": {
								if (!visible) break;
								setStreamStage(event.stage);
								setStreamMessage(event.message ?? null);
								break;
							}
							case "chunk": {
								// Recorded for the conversation regardless of what is on
								// screen, so switching back mid-answer picks it up where it is
								// rather than showing a thread that forgot the question.
								// Recorded even when suppressed below: the server persists the
								// whole answer either way, so dropping it here would make the
								// pending turn disagree with what a restore shows.
								if (streamConversationId) appendPendingTurn(streamConversationId, event.text);
								if (!visible) break;
								if (suppressHarnessEntryOutput) break;
								// First chunk = transition out of "thinking". Avoid spamming
								// setStatus on every chunk; only flip if we're still in thinking.
								if (useAskPerformYardStore.getState().status === "thinking") {
									setStatus("streaming");
								}
								appendToAssistantMessage(assistantMessageId, event.text);
								break;
							}
							case "goals": {
								// Recorded for the conversation regardless of what is on
								// screen, for the same reason as `chunk` above: this event
								// fires once and is never replayed, so the turn's own record
								// is the only place these cards can survive the user reading
								// another thread and coming back.
								if (streamConversationId) {
									recordPendingTurnSuggestions(streamConversationId, assistantMessageId, {
										kind: "goals",
										items: event.suggestions,
									});
								}
								if (!visible) break;
								setGoalSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "goalImprovements": {
								// Recorded for the conversation regardless of what is on
								// screen, for the same reason as `chunk` above: this event
								// fires once and is never replayed, so the turn's own record
								// is the only place these cards can survive the user reading
								// another thread and coming back.
								if (streamConversationId) {
									recordPendingTurnSuggestions(streamConversationId, assistantMessageId, {
										kind: "goalImprovements",
										items: event.suggestions,
									});
								}
								if (!visible) break;
								setGoalImprovements(assistantMessageId, event.suggestions);
								break;
							}
							case "interview_notes": {
								if (!visible) break;
								try {
									sessionStorage.setItem(
										getInterviewerNotesKey(event.harness),
										JSON.stringify(event.notes)
									);
								} catch {
									// sessionStorage write failures are non-fatal
								}
								const enteringCompetencyHarness =
									event.harness === "competency" && effectiveHarness !== "competency";
								setActiveInterviewerHarness(event.harness);
								// A blocked entry streams normally — the agent answers, and the prompt rule carries the fact.
								if (
									enteringCompetencyHarness &&
									!isCompetencyReimportBlocked(useAskPerformYardStore.getState().competencyFramework)
								) {
									markCompetencyIntro(assistantMessageId);
									suppressHarnessEntryOutput = true;
									finalizeMessage(assistantMessageId);
								}
								break;
							}
							case "interviewer_output": {
								if (!visible) break;
								(
									HARNESS_OUTPUT_HANDLERS[event.harness] as (
										event: AskSSEInterviewerOutputEvent,
										msgId: string
									) => void
								)(event, assistantMessageId);
								setStreamMessage(null);
								break;
							}
							case "exit_interviewer": {
								if (!visible) break;
								deactivateInterviewerHarness();
								break;
							}
							case "competency_edit": {
								applyCompetencyEdit(
									{ add: event.add, update: event.update, remove: event.remove },
									assistantMessageId
								);
								break;
							}
							case "forms": {
								// Recorded for the conversation regardless of what is on
								// screen, for the same reason as `chunk` above: this event
								// fires once and is never replayed, so the turn's own record
								// is the only place these cards can survive the user reading
								// another thread and coming back.
								if (streamConversationId) {
									recordPendingTurnSuggestions(streamConversationId, assistantMessageId, {
										kind: "forms",
										items: event.suggestions,
									});
								}
								if (!visible) break;
								setFormSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "formQuestions": {
								// Recorded for the conversation regardless of what is on
								// screen, for the same reason as `chunk` above: this event
								// fires once and is never replayed, so the turn's own record
								// is the only place these cards can survive the user reading
								// another thread and coming back.
								if (streamConversationId) {
									recordPendingTurnSuggestions(streamConversationId, assistantMessageId, {
										kind: "formQuestions",
										items: event.suggestions,
									});
								}
								if (!visible) break;
								setFormQuestionSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "formImprovements": {
								// Recorded for the conversation regardless of what is on
								// screen, for the same reason as `chunk` above: this event
								// fires once and is never replayed, so the turn's own record
								// is the only place these cards can survive the user reading
								// another thread and coming back.
								if (streamConversationId) {
									recordPendingTurnSuggestions(streamConversationId, assistantMessageId, {
										kind: "formImprovements",
										items: event.suggestions,
									});
								}
								if (!visible) break;
								setFormImprovements(assistantMessageId, event.suggestions);
								break;
							}
							case "conversation": {
								// Sent before the first chunk with the id, and again after the
								// turn is saved once a title has been generated. A new
								// conversation carries no title on that first event, which is
								// what keeps the header on "Ask PerformYard" instead of
								// flashing the user's own truncated prompt back at them.
								const announcedId = event.conversationId;
								const heldId = streamConversationId;

								// Snapshotted before the re-key below, because what makes an
								// announcement adoptable is whether the user is still in the chat
								// that sent this turn — which is what `heldId` describes.
								const storeAtAnnouncement = useAskPerformYardStore.getState();
								const adoptable =
									heldId === null
										? // A brand-new chat has no id yet, so the store's id is all
											// there is to compare against — and `null` is not an
											// identity. `resetChat` sets it too, so a New Chat started
											// while this turn was in flight is indistinguishable from
											// the chat that sent it. `sessionId` is the discriminator,
											// because `resetChat` mints a fresh one; without it the
											// abandoned turn re-points the store and localStorage at
											// its own conversation and the user's next message lands
											// there instead of in their new chat.
											storeAtAnnouncement.conversationId === null &&
											storeAtAnnouncement.sessionId === sessionId
										: storeAtAnnouncement.conversationId === heldId;

								if (announcedId !== heldId) {
									// The server does not have to echo the id we sent: a stale or
									// foreign `conversationId` makes `resolveAskConversation` mint a
									// fresh conversation instead. Re-key so the rest of this stream
									// is measured against the conversation it is actually writing
									// to. Leaving `heldId` in place would make `ownsVisibleThread()`
									// false for every remaining event of this stream — chunks would
									// stop rendering, the status would never leave "thinking" (so
									// the composer stays disabled), and `completeAssistantMessage`
									// would never run, leaving the bubble on its streaming cursor.
									//
									// Unconditional, and deliberately independent of `adoptable`:
									// an announcement the store does not adopt still has to move its
									// pending turn, so returning to that conversation later picks
									// the answer up where it is rather than showing a thread that
									// forgot the question.
									const carried = heldId ? storeAtAnnouncement.pendingTurns[heldId] : undefined;
									if (heldId) completePendingTurn(heldId);
									streamConversationId = announcedId;
									// So teardown drops the record this stream actually owns.
									activeStream.conversationId = announcedId;
									startPendingTurn(announcedId, {
										userContent: query,
										assistantMessageId,
										assistantContent: carried?.assistantContent ?? "",
										// Cards already emitted, and the bubble's original
										// timestamp, belong to this turn rather than to the id it
										// was mis-keyed under; leaving them behind would lose
										// exactly what the record exists to keep.
										...(carried
											? {
													suggestionStates: carried.suggestionStates,
													startedAt: carried.startedAt,
												}
											: {}),
									});
								}

								// Adopting is only correct while the user is still in the chat this
								// stream belongs to. Without this, a late event from a conversation
								// they navigated away from silently re-points the store — and
								// localStorage — at it.
								if (!adoptable) break;

								setActiveConversation({
									conversationId: announcedId,
									...(event.title !== undefined ? { title: event.title } : {}),
								});
								break;
							}
							case "responseImprovements": {
								if (streamConversationId) {
									recordPendingTurnSuggestions(streamConversationId, assistantMessageId, {
										kind: "responseImprovements",
										items: event.suggestions,
									});
								}
								if (!visible) break;
								setResponseImprovements(assistantMessageId, event.suggestions);
								break;
							}
							case "done": {
								if (streamConversationId) completePendingTurn(streamConversationId);
								if (visible) {
									completeAssistantMessage(assistantMessageId, event.sources, event.confidence);
								}
								// The exchange is already written server-side by the time `done`
								// is sent, so this refetch cannot race the write. It reorders
								// Recent Conversations and picks up the generated title.
								void queryClient.invalidateQueries({ queryKey: ASK_CONVERSATIONS_QUERY_KEY });
								releaseStream();
								reader.cancel();
								return;
							}
							case "error": {
								if (streamConversationId) completePendingTurn(streamConversationId);
								if (visible) {
									// Finalize the in-flight message so it stops rendering the
									// streaming cursor; the error is surfaced separately via the
									// error banner.
									completeAssistantMessage(assistantMessageId);
									setError(event.message);
								}
								releaseStream();
								reader.cancel();
								return;
							}
						}
					}
				}

				// Stream ended without a done event — the backend likely crashed
				// mid-stream. Complete the message but mark it as incomplete so
				// the UI can show a subtle indicator to the user.
				console.warn("[AskStream] Stream ended without done event");
				if (streamConversationId) completePendingTurn(streamConversationId);
				if (ownsVisibleThread()) {
					completeAssistantMessage(assistantMessageId, undefined, undefined, true);
				}
			} catch (err) {
				if (streamConversationId) completePendingTurn(streamConversationId);

				// Abort isn't an error, but finalize a still-streaming message so its payload isn't stranded.
				if (err instanceof Error && err.name === "AbortError") {
					const messages = getStoreActions().messages;
					const isLast = messages[messages.length - 1]?.id === assistantMessageId;
					if (messages.find((message) => message.id === assistantMessageId)?.isStreaming) {
						completeAssistantMessage(assistantMessageId, undefined, undefined, true);
					} else if (isLast) {
						// Harness-finalized message: completeAssistantMessage never runs to reset the
						// turn. Guarded on isLast so a superseding stream's status isn't clobbered.
						setStatus("idle");
						setStreamStage(null);
						setStreamMessage(null);
					}
					return;
				}

				// An error in a conversation the user has left stays there — surfacing
				// it over the thread they are reading now would be a lie about that
				// thread. The turn is still recoverable from the server on return.
				if (!ownsVisibleThread()) return;

				// If an error occurred while streaming (e.g. lost connection),
				// complete the in-progress message as incomplete so it doesn't
				// show the streaming cursor forever.
				completeAssistantMessage(assistantMessageId, undefined, undefined, true);

				const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
				getStoreActions().setError(errorMessage);
			} finally {
				releaseStream();
				void activeStream.reader?.cancel();
				activeStream.reader = null;
			}
		},
		[formAuthoringCoachEnabled, getStoreActions, queryClient]
	);

	return {
		sendQuery,
		abort,
		isStreaming: status === "thinking" || status === "streaming",
	};
};
